/**
 * Form Analytics - dataLayer events for prescription form tracking
 *
 * Fires events for GA4 funnel analysis without exposing PII or sensitive data.
 */

import { useEffect, useRef } from "react";
import { FlowStep } from "../components/FlowStepRenderer";
import { AccountSubStep, AccountDetailsStep } from "./progress-calculator";

// Step name mappings for non-questionnaire steps
const STEP_NAMES: Partial<Record<FlowStep, string>> = {
  'welcome': 'welcome',
  'state-check': 'state_check',
  'dose-selection': 'dose_selection',
  'quantity-selection': 'quantity_selection',
  'subscription-selection': 'subscription_selection',
  'shipping-info': 'shipping_info',
  'file-upload': 'file_upload',
};

// Account sub-step name mappings (email-capture)
const ACCOUNT_STEP_NAMES: Record<AccountSubStep, string> = {
  'email': 'account_email',
  'login': 'account_login',
};

// Account-details sub-step name mappings (identity/access)
const ACCOUNT_DETAILS_STEP_NAMES: Record<AccountDetailsStep, string> = {
  'identity': 'account_details_identity',
  'access': 'account_details_access',
};

interface FormProgressEvent {
  event: 'prescription_form_progress';
  form_name: string;
  form_id: string;
  step_name: string;
  interaction_type: 'step_view';
}

interface FormCompleteEvent {
  event: 'prescription_form_complete';
  form_name: string;
  form_id: string;
}

/**
 * Convert a string to snake_case for GA4 compatibility
 * Preserves Spanish accented characters, converts to lowercase, replaces spaces with underscores
 */
function toSnakeCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-záéíóúüñ0-9\s]/g, '') // Remove special chars but keep Spanish accents
    .replace(/\s+/g, '_')                // Replace spaces with underscores
    .replace(/_+/g, '_')                 // Remove duplicate underscores
    .replace(/^_|_$/g, '');              // Remove leading/trailing underscores
}

/**
 * Derive form name from product handle
 * @param productHandle - The Shopify product handle
 * @returns "para_el", "para_ella", "for_him", or "for_her"
 */
export function getFormName(productHandle: string): string {
  // Spanish womens
  if (productHandle.includes('para-ella')) {
    return 'para_ella';
  }
  // English womens
  if (productHandle.includes('for-her')) {
    return 'for_her';
  }
  // English mens
  if (productHandle.includes('for-him')) {
    return 'for_him';
  }
  // Default to Spanish mens
  return 'para_el';
}

/**
 * Push event to dataLayer with safety checks
 */
function pushToDataLayer(eventData: FormProgressEvent | FormCompleteEvent): void {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ...eventData });
  }
}

/**
 * Generate a unique key for tracking viewed steps (prevents duplicate events)
 */
function getStepKey(
  step: FlowStep,
  accountSubStep?: AccountSubStep,
  accountDetailsStep?: AccountDetailsStep,
  currentQuestionId?: string
): string {
  if (step === 'email-capture' && accountSubStep) {
    return `${step}:${accountSubStep}`;
  }
  if (step === 'account-details' && accountDetailsStep) {
    return `${step}:${accountDetailsStep}`;
  }
  if (step === 'questionnaire' && currentQuestionId) {
    return `${step}:${currentQuestionId}`;
  }
  return step;
}

/**
 * Get the step name for dataLayer event
 */
function getStepName(
  step: FlowStep,
  accountSubStep?: AccountSubStep,
  accountDetailsStep?: AccountDetailsStep,
  questionTitle?: string
): string | null {
  // Handle email-capture sub-steps
  if (step === 'email-capture' && accountSubStep) {
    return ACCOUNT_STEP_NAMES[accountSubStep] || null;
  }

  // Handle account-details sub-steps
  if (step === 'account-details' && accountDetailsStep) {
    return ACCOUNT_DETAILS_STEP_NAMES[accountDetailsStep] || null;
  }

  // Handle questionnaire - use question title
  if (step === 'questionnaire' && questionTitle) {
    return `questionnaire_${toSnakeCase(questionTitle)}`;
  }

  // Handle other steps
  return STEP_NAMES[step] || null;
}

interface UseFormAnalyticsOptions {
  step: FlowStep;
  productHandle: string;
  formId: string;
  accountSubStep?: AccountSubStep;
  accountDetailsStep?: AccountDetailsStep;
  currentQuestionId?: string;
  currentQuestionTitle?: string;
}

/**
 * Hook to track form step views
 * Fires dataLayer events only once per unique step view
 */
export function useFormAnalytics({
  step,
  productHandle,
  formId,
  accountSubStep,
  accountDetailsStep,
  currentQuestionId,
  currentQuestionTitle,
}: UseFormAnalyticsOptions): { trackFormComplete: () => void } {
  // Track which steps have been viewed to prevent duplicate events
  const viewedStepsRef = useRef<Set<string>>(new Set());
  const formName = getFormName(productHandle);

  useEffect(() => {
    const stepKey = getStepKey(step, accountSubStep, accountDetailsStep, currentQuestionId);

    // Skip if already tracked
    if (viewedStepsRef.current.has(stepKey)) {
      return;
    }

    // Get step name for this step
    const stepName = getStepName(step, accountSubStep, accountDetailsStep, currentQuestionTitle);

    // Skip steps we don't track (checkout is handled separately)
    if (!stepName) {
      return;
    }

    // Mark as viewed
    viewedStepsRef.current.add(stepKey);

    // Push event to dataLayer
    pushToDataLayer({
      event: 'prescription_form_progress',
      form_name: formName,
      form_id: formId,
      step_name: stepName,
      interaction_type: 'step_view',
    });
  }, [step, accountSubStep, accountDetailsStep, currentQuestionId, currentQuestionTitle, formName, formId]);

  // Return function to manually track form completion
  const trackFormComplete = () => {
    pushToDataLayer({
      event: 'prescription_form_complete',
      form_name: formName,
      form_id: formId,
    });
  };

  return { trackFormComplete };
}

/**
 * Standalone function to track form completion
 * Use this in async functions where the hook can't be used
 */
export function trackFormComplete(productHandle: string, formId: string): void {
  const formName = getFormName(productHandle);
  pushToDataLayer({
    event: 'prescription_form_complete',
    form_name: formName,
    form_id: formId,
  });
}
